/**
 * Geometry shared by the ambient creatures.
 *
 * World Y spans fewer pixels than world X, so every creature-to-player or
 * creature-to-anchor comparison has to divide Y by the aspect before it means
 * anything on screen. One implementation, so the rabbit and the bird agree on
 * what "close" is.
 */

import type { SpawnPoint } from '@/world-prototype/types';

/** Distance between two world points, in world-WIDTH units. */
export function creatureDistance(
  a: SpawnPoint,
  b: SpawnPoint,
  worldAspect: number,
): number {
  const aspect = worldAspect > 0 ? worldAspect : 1;
  const dx = a.x - b.x;
  const dy = (a.y - b.y) / aspect;
  return Math.hypot(dx, dy);
}

/**
 * Step `from` toward `to` by `distance` world-width units, honouring the same
 * aspect correction so movement looks straight rather than squashed.
 */
export function stepToward(
  from: SpawnPoint,
  to: SpawnPoint,
  distance: number,
  worldAspect: number,
): SpawnPoint {
  const aspect = worldAspect > 0 ? worldAspect : 1;
  const dx = to.x - from.x;
  const dy = (to.y - from.y) / aspect;
  const span = Math.hypot(dx, dy);
  if (span <= 1e-6 || distance >= span) return { x: to.x, y: to.y };
  const t = distance / span;
  return { x: from.x + dx * t, y: from.y + dy * t * aspect };
}
